import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';

import { Card } from 'core/components/card';
import Label from 'core/components/label/label';
import Button from 'core/components/button/button';
import { Loading } from 'core/components/loading';
import { useStore } from 'core/store';
import { track } from 'core/helpers/analytics';
import AutoIcon from 'core/assets/svg/ic-auto.svg';
import BranchLogo from 'core/assets/svg/branch-logo.svg';

import useStyles from './branch-auto-upsell.styles';
import { staffOfferLink } from 'core/helpers/bridge-link-opened';

// Hardcoded — the lambda rates against prod `quote-to-bind-api` in every env, so the
// resulting offer always lives in prod and only prod staff can render it. Sidebars
// already gate on `!isBranchEnv`, so we never reach this URL on a Branch deploy.
const BRANCH_STAFF_URL = 'https://staff.ourbranch.com';

export const BranchAutoUpsell = observer(function BranchAutoUpsell({ input, source, onHide }) {
  const classes = useStyles();
  const { additionalOffers } = useStore();

  useEffect(() => {
    if (!input) {
      return;
    }
    additionalOffers.loadBixAutoUpsell(input);
  }, [additionalOffers, input?.branchOfferId, input?.branchPolicyId]);

  const state = additionalOffers.bixAutoUpsell;
  const hidden = state?.status === 'hidden';

  useEffect(() => {
    if (hidden) {
      onHide?.();
    }
  }, [hidden, onHide]);

  if (!input || hidden) {
    return null;
  }

  const result = state?.status === 'success' ? state : null;
  const loading = !result;

  const handleClick = () => {
    if (!result?.offerId) {
      return;
    }
    track('Agent Clicked BIX Auto Upsell', {
      source,
      url: window.location.href,
      offerId: result.offerId
    });
    window.open(`${BRANCH_STAFF_URL}${staffOfferLink(result.offerId)}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <Card id="branch_auto_upsell" className={classes.card}>
        <Grid container alignItems="center" justifyContent="center" className={classes.loadingRow}>
          <Loading noBackground size={28} />
        </Grid>
      </Card>
    );
  }

  const hasPrice = result.autoMonthlyPremium !== null && result.autoMonthlyPremium !== undefined;

  return (
    <Card id="branch_auto_upsell" className={classes.card}>
      <Label className={classes.title}>Auto insurance offer</Label>
      <div className={classes.innerCard}>
        <div className={classes.innerCardTop}>
          <div>
            <Label className={classes.innerCardTopTitle}>Personalized auto offer</Label>
            <Label className={classes.innerCardTopSubtitle}>
              {input.firstName} {input.lastName}
            </Label>
          </div>
          <div>
            <img src={AutoIcon} className={classes.icon} alt="" />
          </div>
        </div>
        <div className={classes.innerCardBottom}>
          {hasPrice ? (
            <div>
              <Label className={classes.innerCardBottomTitle}>Price</Label>
              <Label className={classes.innerCardBottomValue}>
                $
                {result.autoMonthlyPremium.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2
                })}
                <span className={classes.pricePerMonthText}>/ MO</span>
              </Label>
            </div>
          ) : (
            <div>
              <Label className={classes.priceAvailableText}>Price available inside</Label>
            </div>
          )}
          <div className={classes.branchBranding}>
            <div className={classes.divider} />
            <img src={BranchLogo} className={classes.branchLogo} alt="Branch" />
          </div>
        </div>
      </div>
      <div className={classes.actionSection}>
        <Button mode="big" variant="contained" color="secondary" onClick={handleClick}>
          See offer
        </Button>
      </div>
    </Card>
  );
});

BranchAutoUpsell.propTypes = {
  input: PropTypes.shape({
    branchOfferId: PropTypes.string,
    branchPolicyId: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    email: PropTypes.string,
    phone: PropTypes.string
  }).isRequired,
  source: PropTypes.oneOf(['offer', 'customer']).isRequired,
  onHide: PropTypes.func
};
